import differenceWith from "lodash/differenceWith";
import take from "lodash/take";
import { graphql, readInlineData } from "react-relay";

import type {
  helpersSectionStoryStyleGetDeDuplicatedCombinedArticleListQuery$data,
  helpersSectionStoryStyleGetDeDuplicatedCombinedArticleListQuery$key,
} from "~/queries/__generated__/helpersSectionStoryStyleGetDeDuplicatedCombinedArticleListQuery.graphql";

import type { SubSection } from "./consts";
import { MaximumArticleNumberPerSection, SectionNameMap } from "./consts";
import type { Section } from "./types";

export type UniqueArticlesPerSectionItem = {
  articles: ArticleList;
  section: Section;
};

// Remember to check the queue in Drupal CMS and click the save to sync the articles(For staging).
export function getUniqueArticlesPerSection(parameters: GetDeDuplicatedArticleListParameters) {
  const { currentSectionEntityId, reference } = parameters;

  const data = readInlineData(query, reference);

  const subSectionToArticleListPairList: [SubSection, ArticleList][] = [
    ["trends", getArticleList({ queue: data.trends })],
    ["luxury", getArticleList({ queue: data.luxury })],
    ["celeb", getArticleList({ queue: data.celeb })],
    ["kpop", getArticleList({ queue: data.kpop })],
    ["leisure", getArticleList({ queue: data.leisure })],
    ["well", getArticleList({ queue: data.well })],
    ["tech", getArticleList({ queue: data.tech })],
  ];

  const uniqueArticlesPerSection = subSectionToArticleListPairList.reduce(
    (result: UniqueArticlesPerSectionItem[], [sectionNameKey, currentArticleList]) => {
      const filteredArticleList = take(
        differenceWith(
          currentArticleList,
          result.flatMap(({ articles }) => articles),
          (currentArticle, selectedArticle) => currentArticle.entityId === selectedArticle.entityId,
        ),
        MaximumArticleNumberPerSection,
      );

      return [
        ...result,
        {
          articles: filteredArticleList,
          section: SectionNameMap[sectionNameKey],
        },
      ];
    },
    [],
  );

  const matchedSectionIndex = uniqueArticlesPerSection.findIndex(
    ({ section }) => section.entityId === currentSectionEntityId,
  );

  if (matchedSectionIndex === -1) {
    // If the current section is not found, return the default ordering, e.g. /magazines/style.
    return uniqueArticlesPerSection;
  }

  return [
    ...uniqueArticlesPerSection.slice(matchedSectionIndex),
    ...uniqueArticlesPerSection.slice(0, matchedSectionIndex),
  ];
}

type GetArticleListParameters = {
  queue: helpersSectionStoryStyleGetDeDuplicatedCombinedArticleListQuery$data[SubSection];
};

export type ArticleList = ReturnType<typeof getArticleList>;

type GetDeDuplicatedArticleListParameters = {
  currentSectionEntityId: string;
  reference: helpersSectionStoryStyleGetDeDuplicatedCombinedArticleListQuery$key;
};

function getArticleList(parameters: GetArticleListParameters) {
  const { queue } = parameters;
  const edgeList = queue?.items?.edges ?? [];
  return edgeList.map(edge => edge.node) ?? [];
}

const query = graphql`
  fragment helpersSectionStoryStyleGetDeDuplicatedCombinedArticleListQuery on Query @inline {
    trends: queue(filter: { entityId: "1060" }) {
      items(first: 4) {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
    luxury: queue(filter: { entityId: "1070" }) {
      items(first: 8) {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
    celeb: queue(filter: { entityId: "1087" }) {
      items(first: 8) {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
    kpop: queue(filter: { entityId: "section_top_507629" }) {
      items(first: 8) {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
    leisure: queue(filter: { entityId: "1082" }) {
      items(first: 8) {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
    well: queue(filter: { entityId: "section_top_501517" }) {
      items(first: 8) {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
    tech: queue(filter: { entityId: "1075" }) {
      items(first: 8) {
        __typename
        edges {
          node {
            ... on Article {
              entityId
              ...storyStyleCardArticle
              ...storyCardCarouselItemArticle
            }
          }
        }
      }
    }
  }
`;
